/* eslint-disable prettier/prettier */
import React, { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast, ToastContainer } from 'react-toastify'
import CN from 'classnames'
import {
  useGetCutoffTime,
  useGetManifestListByCourierAndWarehouse,
  useGetWarehouseTable,
} from 'framework/api/methods'
import { useAusPostManifest } from 'framework/api/methods/manifest/useAusPostManifest'

import { SimpleSelect } from 'components/atoms'
import { Button } from 'components/atoms/Button'

import { DataTable } from './AusPostManifestDataTable'

export interface AusPostManifestingProps {
  [x: string]: any
}

export const AusPostManifesting: FC<AusPostManifestingProps> = ({
  className,
  ...restProps
}: AusPostManifestingProps) => {
  const notify = (message: any, type: any) => {
    if (type === 'error') {
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    }
    if (type === 'success') {
      toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    }
  }

  const [wareHouseList, setWareHouseList] = useState([] as any)
  const [wareHouse, setWarehouse] = useState<any>({
    label: 'All',
    value: 'All',
  })
  const [carrier, setCarrier] = useState<any>({ label: 'All', value: 'AUPOST' })
  const [cutoffTime, setCutoffTime] = useState<any>(null)
  const [isManifestOrderEnable, setIsManifestOrderEnable] = useState(false)
  const [tableVariables, setTableVariables] = useState<any>({
    take: 10,
    skip: 0,
    Search: '',
  })
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  /** Function On-Error Get Currier Products*/
  const getManifestListByCourierAndWarehouseOnError = ({
    response: { data: errData },
  }: any) => {
    errData.message && notify(errData.message, 'error')
  }

  /** API Call for get manifest list function using react-query */
  const { isLoading, isFetching, data, refetch } =
    useGetManifestListByCourierAndWarehouse(
      {
        ...tableVariables,
        courierCode: carrier?.value || '',
        subCourier: '',
        warehouseCode: wareHouse?.value || '',
      },
      getManifestListByCourierAndWarehouseOnError,
    )

  /** API Call for get cutoff time using react-query */
  const { mutate: getCutOffTimeMutate } = useGetCutoffTime()

  /** Process the get cutoff time */
  async function getCutoffTime() {
    getCutOffTimeMutate(
      {
        warehouseCode: wareHouse?.value,
        courierCode: carrier?.value,
        subCourierCode: null,
      },
      {
        onSuccess: ({ data: successData }: any) => {
          successData?.cutoffTime?.status &&
            setCutoffTime(successData?.cutoffTime?.cutofffTime)
        },
        onError: ({ response: { data: errData } }: any) => {
          console.log('error')
        },
      },
    )
  }

  const { mutate: getWarehouseListMutate } = useGetWarehouseTable()

  /** Process to get Warehouse List for integration */
  async function getWarehouseList(): Promise<void> {
    getWarehouseListMutate(
      { take: 100, skip: 0, name: '', isActive: true },
      {
        onSuccess: ({ data: successData }: any) => {
          const tempStateList = successData?.warehouses?.map((item: any) => {
            return {
              ...item,
              value: item.code,
              label: `${item.code} - ${item.name}`,
              id: item.id,
            }
          })
          setWareHouseList(tempStateList)
        },
        onError: ({ response: { data: errData } }: any) => {
          notify(errData.message || 'Error', 'error')
        },
      },
    )
  }

  useEffect(() => {
    getWarehouseList()
  }, [])

  useEffect(() => {
    wareHouse?.value !== 'All' && carrier?.value !== 'All' && getCutoffTime()
  }, [wareHouse?.value, carrier?.value])

  /** check now time is between cutoff time and duration range*/
  const isNowBetweenTimes = (timeString: any) => {
    const [hours, minutes, period] = timeString.split(/:| /)
    let hoursNum = parseInt(hours)
    const minutesNum: any = parseInt(minutes)

    if (period === 'PM' && hoursNum !== 12) {
      hoursNum += 12
    } else if (period === 'AM' && hoursNum === 12) {
      hoursNum = 0
    }

    const originalTime = new Date()
    originalTime.setHours(hoursNum)
    originalTime.setMinutes(minutesNum)

    // NOTE : as per the client requirement Default Time duration is 30 minutes
    const newTime = new Date(originalTime)
    newTime.setMinutes(originalTime.getMinutes() + 30)

    const now = new Date()
    const isBetweenTimes = now >= originalTime && now <= newTime
    return isBetweenTimes
  }

  useEffect(() => {
    cutoffTime && setIsManifestOrderEnable(isNowBetweenTimes(cutoffTime))
  }, [cutoffTime])

  /** API Call for manifest by tracking numbers using react-query */
  const { mutate: ausPostManifestMutate, isLoading: ausPostManifestIsLoading } =
    useAusPostManifest()

  /** Process the  manifest by tracking numbers */
  async function manifestByTrackingNumbers() {    
    const wmsOrderNumbersList: string[] = []
    data?.manifestOrders.forEach((element: { wmsOrder: any }) => {
      wmsOrderNumbersList.push(element.wmsOrder)
    })
    ausPostManifestMutate(
      {
        WhOrderNos: wmsOrderNumbersList,
        CourierCode: carrier?.value,
        WhCode: wareHouse?.value,
      },
      {
        onSuccess: ({ data: successData }: any) => {
          notify(successData.message, 'success')          
          setTimeout(() => { refetch(), setIsButtonDisabled(false) }, 4000)
        },
        onError: ({ response: { data: errData } }: any) => {
          notify(errData.message || 'Error', 'error')
          setIsButtonDisabled(false)
        },
      },
    )
  }

  const { userDetails }: any = useSelector<any>((state) => state.user)

  // ---- Get the current date Format the date ---- //
  const today = new Date()

  const day = today.getDate()
  const month = today.toLocaleString('default', { month: 'long' })

  const getDayWithSuffix = (day: any) => {
    if (day >= 11 && day <= 13) {
      return day + 'th'
    }
    switch (day % 10) {
      case 1:
        return day + 'st'
      case 2:
        return day + 'nd'
      case 3:
        return day + 'rd'
      default:
        return day + 'th'
    }
  }

  const formattedDate = getDayWithSuffix(day) + ' ' + month

  const permissionForManifestOrder = userDetails?.userPermissions?.includes(
    'AusPostManifestAccess',
  )
  return (
    <div
      className={CN(
        'flex items-center justify-center bg-N-50 h-full w-full p-4',
        {},
        className,
      )}
      {...restProps}>
      <div className='flex flex-col w-full h-full px-5 bg-white rounded-lg shadow-md'>
        <div className='py-[28px]'>
          <h2 className='text-lg font-semibold'>
            Manifest Australia Post Orders for {formattedDate}
          </h2>
        </div>
        <div className='flex justify-between items-end'>
          <div className='flex gap-x-4 w-1/2'>
            <div className='flex flex-col w-1/2 z-[2]'>
              <span className='pb-1 text-N-700'>Australia Post Courier</span>
              <SimpleSelect
                value={carrier || null}
                onChange={(e: any) => {
                  setCarrier({
                    value: e.value,
                    label: e.label,
                  })
                }}
                placeholder='AusPost Carrier'
                options={[
                  { label: 'All', value: 'AUPOST' },
                  { label: 'Australia Post Domestic', value: 'AUSPOST' },
                  {
                    label: 'Australia Post International',
                    value: 'AUSPOST_INTERNATIONAL',
                  },
                ]}
              />
            </div>

            <div className='flex flex-col w-1/2 z-[2]'>
              <span className='pb-1 text-N-700'>Warehouse Name</span>

              <SimpleSelect
                value={wareHouse || null}
                onChange={(e: any) => {
                  setWarehouse({
                    value: e.value,
                    label: e.label,
                  })
                }}
                placeholder='Warehouse'
                options={[{ label: 'All', value: 'All' }, ...wareHouseList]}
              />
            </div>
          </div>
          {permissionForManifestOrder && (
            <div className='flex gap-x-5'>
              {data?.manifestFile && (
                <Button
                  appearance='outline'
                  onClick={() => {
                    const pdfUrl = data?.manifestFile

                    const downloadLink = document.createElement('a')
                    downloadLink.href = pdfUrl
                    downloadLink.target = '_blank'
                    downloadLink.download = 'manifestFile.pdf'

                    downloadLink.click()
                  }}>
                  Manifest Report Download
                </Button>
              )}
              <Button
                isLoading={ausPostManifestIsLoading}
                disabled={
                  isButtonDisabled || // Disable button during process
                  !(
                    isManifestOrderEnable && data?.manifestOrders?.length > 0
                  ) ||
                  data?.manifestOrders?.some((item: any) => item.isManifest) ||
                  carrier?.value === 'All' ||
                  wareHouse?.value === 'All'
                }
                onClick={() => {
                  if (isNowBetweenTimes(cutoffTime)) {
                    !ausPostManifestIsLoading && manifestByTrackingNumbers()
                    setIsButtonDisabled(true)
                  } else {
                    notify(
                      'Manifesting is disabled as the cutoff period is exceeded.',
                      'error',
                    )
                  }
                }}>
                Manifest Order
              </Button>
            </div>
          )}
        </div>
        <div className='w-full h-[calc(100%-160px)] pt-4'>
          <DataTable
            itemData={data?.manifestOrders || []}
            setTableVariables={setTableVariables}
            tableVariables={tableVariables}
            totalRowCount={data?.totalCount}
            isLoading={isLoading || isFetching}
            isManifesting={ausPostManifestIsLoading}
          />
        </div>
      </div>

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='colored'
        className='toast-container'
      />
    </div>
  )
}

AusPostManifesting.defaultProps = {}

export default AusPostManifesting
